import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@iq/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Role = 'admin' | 'auditor' | 'operator' | 'viewer';

export function UsersTable() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession();
  const currentUserId = session.status === 'authenticated' ? session.session.userId : null;
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const { data, error: apiError } = await api.GET('/admin/users');
      if (apiError) throw apiError;
      return data;
    },
  });

  const setRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: Role }) => {
      const { data, error: apiError } = await api.PATCH('/admin/users/{userId}/role', {
        params: { path: { userId } },
        body: { role },
      });
      if (apiError) throw apiError;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (err: { detail?: string; title?: string }) =>
      setError(err.detail ?? err.title ?? 'Role change failed'),
  });

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      const { error: apiError } = await api.DELETE('/admin/users/{userId}', {
        params: { path: { userId } },
      });
      if (apiError) throw apiError;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (err: { detail?: string; title?: string }) =>
      setError(err.detail ?? err.title ?? 'Delete failed'),
  });

  if (usersQuery.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (usersQuery.isError || !usersQuery.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('admin.error')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-users-table">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.title')}</h1>
        <Badge variant="secondary">{t('admin.totalCount', { count: usersQuery.data.total })}</Badge>
      </header>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.columns.email')}</TableHead>
            <TableHead>{t('admin.columns.name')}</TableHead>
            <TableHead>{t('admin.columns.role')}</TableHead>
            <TableHead>{t('admin.columns.status')}</TableHead>
            <TableHead className="text-right">{t('admin.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {usersQuery.data.users.map((user) => (
            <TableRow key={user.id} data-testid={`admin-row-${user.id}`}>
              <TableCell className="font-medium">{user.email}</TableCell>
              <TableCell className="text-muted-foreground">{user.name ?? '—'}</TableCell>
              <TableCell>
                <Select
                  value={user.role}
                  onValueChange={(value) =>
                    setRole.mutate({ userId: user.id, role: value as Role })
                  }
                  // Users cannot change their own role; the server also
                  // rejects it with 400, so the control is disabled to avoid
                  // a failed request.
                  disabled={user.id === currentUserId}
                >
                  <SelectTrigger
                    className="w-32"
                    aria-label={t('admin.columns.role')}
                    data-testid={`admin-role-trigger-${user.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">viewer</SelectItem>
                    <SelectItem value="auditor">auditor</SelectItem>
                    <SelectItem value="operator">operator</SelectItem>
                    <SelectItem value="admin">admin</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                {user.banned ? (
                  <Badge variant="destructive">{t('admin.banned')}</Badge>
                ) : (
                  <Badge variant="outline">{t('admin.active')}</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                {user.id === currentUserId ? (
                  <span className="text-xs text-muted-foreground">{t('admin.you')}</span>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('admin.delete')}
                        data-testid={`admin-delete-trigger-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('admin.deleteConfirmTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('admin.deleteConfirmBody', { email: user.email })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('admin.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteUser.mutate(user.id)}
                          data-testid={`admin-delete-confirm-${user.id}`}
                        >
                          {t('admin.delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
